// src/shared/components/ui/Dropdown.js

import React, { useState, useRef, useEffect } from 'react';
import { createPortal } from 'react-dom';

const Dropdown = ({
    trigger,
    children,
    align = 'right',
    className = ''
}) => {
    const [isOpen, setIsOpen] = useState(false);
    const [position, setPosition] = useState({ top: 0, left: 0, right: 0 });
    const dropdownRef = useRef(null);
    const triggerRef = useRef(null);

    // Calcular posición del dropdown
    useEffect(() => {
        if (isOpen && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            const scrollY = window.scrollY;
            const scrollX = window.scrollX;
            
            if (align === 'right') {
                setPosition({
                    top: rect.bottom + scrollY + 8,
                    right: window.innerWidth - rect.right - scrollX,
                    left: undefined
                });
            } else if (align === 'left') {
                setPosition({
                    top: rect.bottom + scrollY + 8,
                    left: rect.left + scrollX,
                    right: undefined
                });
            } else {
                setPosition({
                    top: rect.bottom + scrollY + 8,
                    left: rect.left + scrollX + (rect.width / 2),
                    right: undefined
                });
            }
        }
    }, [isOpen, align]);

    // Cerrar dropdown al hacer click fuera
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target) &&
                triggerRef.current && !triggerRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };

        if (isOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isOpen]);

    const alignmentClasses = {
        left: 'left-0',
        right: 'right-0',
        center: 'left-1/2 transform -translate-x-1/2'
    };

    return (
        <>
            <div className={`relative inline-block ${className}`}>
                {/* Trigger del dropdown */}
                <div ref={triggerRef} onClick={() => setIsOpen(!isOpen)}>
                    {trigger}
                </div>
            </div>

            {/* Menu del dropdown - renderizado con portal para evitar problemas de z-index */}
            {isOpen && createPortal(
                <div
                    ref={dropdownRef}
                    className="fixed w-56 rounded-lg shadow-lg bg-white ring-1 ring-black ring-opacity-5 z-[9999]"
                    style={{
                        top: `${position.top}px`,
                        ...(position.right !== undefined ? { right: `${position.right}px` } : {}),
                        ...(position.left !== undefined && align === 'left' ? { left: `${position.left}px` } : {}),
                        ...(position.left !== undefined && align === 'center' ? { left: `${position.left}px`, transform: 'translateX(-50%)' } : {})
                    }}
                >
                    <div className="py-1">
                        {children}
                    </div>
                </div>,
                document.body
            )}
        </>
    );
};

// Componente para los items del dropdown
export const DropdownItem = ({
    icon,
    children,
    onClick,
    variant = 'default',
    disabled = false
}) => {
    const variantClasses = {
        default: 'text-gray-700 hover:bg-gray-100',
        primary: 'text-blue-600 hover:bg-blue-50',
        danger: 'text-red-600 hover:bg-red-50',
        success: 'text-green-600 hover:bg-green-50'
    };

    return (
        <button
            onClick={onClick}
            disabled={disabled}
            className={`w-full text-left px-4 py-2 text-sm flex items-center gap-2 transition-colors ${
                disabled
                    ? 'text-gray-400 cursor-not-allowed'
                    : variantClasses[variant]
            }`}
        >
            {icon && <span className="text-lg">{icon}</span>}
            <span>{children}</span>
        </button>
    );
};

// Componente para separadores en el dropdown
export const DropdownDivider = () => {
    return <div className="border-t border-gray-200 my-1" />;
};

export default Dropdown;
